import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { nanoid } from 'nanoid';

import { Close, DeleteOutline, Nfc, Pause, Save } from '@mui/icons-material';
import {
	Alert,
	Autocomplete,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormGroup,
	IconButton,
	LinearProgress,
	List,
	ListItem,
	ListItemText,
	TextField,
	Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';

import moment, { Moment } from 'moment';

import { currency, getErrorMessage } from 'lib/helpers';
import { addBatchUid, createRecentReadFilter, removeBatchUid } from 'lib/nfcCardBatch';
import {
	NFC_BATCH_MAX_CARDS,
	NFC_CARD_LABEL_MAX_LENGTH,
	NfcCardBatchResult,
	NfcCardBatchTemplate,
	createNfcCardsBatch,
	getCardFailures,
	getLoadTotal,
	isRefusedByApi,
} from 'lib/models/nfcCards';

import useIsMobile from 'hooks/useIsMobile';
import useNfcCardCart from 'hooks/useNfcCardCart';
import useNfcReader from 'hooks/useNfcReader';

import ConfirmationDialog from 'components/ConfirmationDialog';
import NfcCardCartPicker from 'components/NfcCardCartPicker';
import NfcCardFailures from 'components/NfcCardFailures';

type Props = {
	/** The labels the cards already have, suggested for the batch. */
	labels: string[];
	/** The responsables the cards already have, suggested for the batch. */
	responsables: string[];
	/** Called when the batch is over, telling whether cards may have been created, so the listing is read again. */
	onClose: (changed: boolean) => void;
};

/**
 * A refused batch. The API names each refused card by its position in the request, never by
 * its UID (MUTE-606); the modal ties it back to the UID it sent, which it keeps to itself.
 */
type BatchError = { message: string; failures: { uid: string; reason: string }[] | null };

/**
 * Registers a batch of cards with Web NFC, in three steps on one screen.
 *
 * First the template: label, responsable and expiration, and optionally a balance and the
 * products to load on every card. Then "Leer tarjetas": cards are brought near the phone one
 * after another and only gathered in a list, nothing is written; a card read again is
 * ignored, and any card can be taken off the list. Last, "Crear las N tarjetas" sends the
 * whole batch in a single request, which the API writes all or nothing: when it refuses, it
 * names the cards and why, and none was created.
 *
 * The batch key is drawn once, when the modal opens. When an attempt gets no answer (the
 * network, a 5xx) it may have been written anyway, so the template and the list are frozen
 * and the only way on is to retry it as it was: the API answers what it already wrote
 * instead of writing it twice. A refusal changed nothing, so after one everything can be
 * edited and sent again with the same key.
 *
 * Only opened where the browser can read NFC. The read stops when the modal closes.
 */
export default function NfcCardBatchModal({ labels, responsables, onClose }: Props) {
	const { t } = useTranslation();
	const isMobile = useIsMobile();

	const [batchKey] = useState(() => `nfc-batch-${nanoid()}`);

	const [label, setLabel] = useState('');
	const [responsable, setResponsable] = useState('');
	const [expiresAt, setExpiresAt] = useState<Moment | null>(null);
	const [balanceText, setBalanceText] = useState('');

	const cart = useNfcCardCart();

	const [uids, setUids] = useState<string[]>([]);
	const [isNewRead] = useState(createRecentReadFilter);

	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<BatchError | null>(null);
	// The last attempt got no answer: it may have been written, so it can only be retried as it was
	const [uncertain, setUncertain] = useState(false);
	const [results, setResults] = useState<NfcCardBatchResult[] | null>(null);

	const [showDiscard, setShowDiscard] = useState(false);

	const locked = saving || uncertain;

	const collect = (uid: string) => {
		if (!isNewRead(uid)) return;
		setUids((current) => addBatchUid(current, uid));
	};

	const { scanning, error: readError, start, stop } = useNfcReader({ onUid: collect, continuous: true });

	const remove = (uid: string) => {
		setUids((current) => removeBatchUid(current, uid));
	};

	const balance = parseAmount(balanceText);
	const brokenBalance = balanceText.trim() !== '' && balance === null;

	// A card is usable through the whole day it is set to expire on, so today is still fine
	const brokenDate = !!expiresAt && (!expiresAt.isValid() || expiresAt.isBefore(moment().startOf('day')));

	// A section picked with nothing of it is neither a load nor the lack of one
	const brokenLoad = !!cart.section && (cart.cart.length === 0 || cart.missingMixer);

	const tooManyCards = uids.length > NFC_BATCH_MAX_CARDS;

	const canCreate =
		uids.length > 0 && !tooManyCards && !brokenBalance && !brokenDate && !brokenLoad && !cart.loading && !saving;

	const failureOf = (uid: string) => error?.failures?.find((failure) => failure.uid === uid)?.reason;

	// The cards are listed newest first, and named by the order they were read in: never by their UID
	const cardName = (index: number) => t('nfc:cardNumber', { number: uids.length - index });

	const shownFailures =
		error?.failures
			?.map((failure) => ({ index: uids.indexOf(failure.uid), reason: failure.reason }))
			.filter((failure) => failure.index >= 0) ?? null;

	const create = async () => {
		if (!canCreate) return;

		stop();
		setSaving(true);
		setError(null);

		const template: NfcCardBatchTemplate = {
			label: label.trim() || null,
			responsable: responsable.trim() || null,
			expiresAt: expiresAt ? expiresAt.clone().endOf('day').toISOString() : null,
			balance,
			load:
				cart.section?._id && cart.cart.length > 0
					? { section: cart.section._id, products: cart.cart, total: cart.total }
					: null,
		};

		const sent = uids;
		try {
			setResults(await createNfcCardsBatch(sent, template, batchKey));
			setUncertain(false);
		} catch (createError) {
			const failures = getCardFailures(createError);
			setError({
				message: getErrorMessage(createError),
				failures: failures?.map((failure) => ({ uid: sent[failure.index] ?? '', reason: failure.reason })) ?? null,
			});

			if (isRefusedByApi(createError)) {
				setUncertain(false);

				// A refusal that is not about the cards may be about the catalogue (a price, a title,
				// a product out of the section): read it again so the cart shows what it is now
				if (!failures && template.load) {
					cart.refetchCatalogue();
				}
			} else {
				setUncertain(true);
			}
		}

		setSaving(false);
	};

	const close = () => {
		if (saving) return;

		if (!results && !uncertain && uids.length > 0) {
			setShowDiscard(true);
			return;
		}

		stop();
		onClose(!!results || uncertain);
	};

	const onDiscard = (confirmed: boolean) => {
		setShowDiscard(false);
		if (confirmed) {
			stop();
			onClose(false);
		}
	};

	const created = results?.filter((result) => result.created).length ?? 0;
	const serverBalance = results?.find((result) => typeof result.balance === 'number')?.balance;
	const serverTotal = getLoadTotal(results?.find((result) => !!result.orders?.length)?.orders);

	return (
		<Dialog open onClose={close} fullWidth maxWidth="md" fullScreen={isMobile}>
			{(saving || cart.loading) && <LinearProgress />}

			{showDiscard && (
				<ConfirmationDialog
					title={t('nfc:batchDiscardTitle')}
					description={t('nfc:batchDiscardDescription', { count: uids.length })}
					confirmButton={t('nfc:batchDiscard')}
					onClose={onDiscard}
				/>
			)}

			<DialogTitle
				sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}
			>
				<div>{t('nfc:batchTitle')}</div>
				<IconButton aria-label={t('common:close')} onClick={close} edge="end" disabled={saving}>
					<Close />
				</IconButton>
			</DialogTitle>

			<DialogContent>
				{results ? (
					<FormGroup sx={{ paddingTop: 1 }}>
						<Alert severity="success" sx={{ mb: 2 }}>
							<Typography>{t('nfc:batchCreatedCount', { count: results.length })}</Typography>
							{typeof serverBalance === 'number' && (
								<Typography>{t('nfc:batchCreatedBalance', { amount: currency(serverBalance) })}</Typography>
							)}
							{typeof serverTotal === 'number' && (
								<Typography>{t('nfc:batchCreatedProducts', { total: currency(serverTotal) })}</Typography>
							)}
						</Alert>

						{created < results.length && (
							<Alert severity="info" sx={{ mb: 2 }}>
								{t('nfc:batchRetried', { count: results.length - created })}
							</Alert>
						)}
					</FormGroup>
				) : (
					<FormGroup sx={{ paddingTop: 1 }}>
						<Typography variant="body2" sx={{ mb: 2 }}>
							{t('nfc:batchHelp')}
						</Typography>

						{uncertain && (
							<Alert severity="warning" sx={{ mb: 2 }}>
								{t('nfc:batchUncertain')}
							</Alert>
						)}

						{!!error && <NfcCardFailures message={error.message} failures={shownFailures} cardName={cardName} />}

						<Typography variant="h6" sx={{ mb: 2 }}>
							{t('nfc:batchTemplate')}
						</Typography>

						<Autocomplete
							freeSolo
							options={labels}
							inputValue={label}
							onInputChange={(_, value) => setLabel(value)}
							disabled={locked}
							renderInput={(params) => (
								<TextField
									{...params}
									label={t('nfc:label')}
									helperText={t('nfc:batchOptionalHelp')}
									inputProps={{ ...params.inputProps, maxLength: NFC_CARD_LABEL_MAX_LENGTH }}
								/>
							)}
							sx={{ mb: 3 }}
						/>

						<Autocomplete
							freeSolo
							options={responsables}
							inputValue={responsable}
							onInputChange={(_, value) => setResponsable(value)}
							disabled={locked}
							renderInput={(params) => (
								<TextField
									{...params}
									label={t('nfc:responsable')}
									helperText={t('nfc:batchOptionalHelp')}
									inputProps={{ ...params.inputProps, maxLength: NFC_CARD_LABEL_MAX_LENGTH }}
								/>
							)}
							sx={{ mb: 3 }}
						/>

						<DatePicker
							sx={{ width: '100%', mb: 3 }}
							value={expiresAt}
							label={t('nfc:expiresAt')}
							format="DD/MM/YYYY"
							disablePast
							onChange={(value) => setExpiresAt(value)}
							disabled={locked}
							slotProps={{
								field: { clearable: true },
								textField: {
									error: brokenDate,
									helperText: brokenDate ? t('nfc:batchDateInvalid') : t('nfc:batchOptionalHelp'),
								},
							}}
						/>

						<TextField
							label={t('nfc:batchBalance')}
							value={balanceText}
							onChange={(event) => setBalanceText(event.target.value)}
							disabled={locked}
							error={brokenBalance}
							helperText={brokenBalance ? t('nfc:balanceAmountInvalid') : t('nfc:batchBalanceHelp')}
							inputProps={{ inputMode: 'decimal' }}
							sx={{ mb: 3 }}
						/>

						<Typography variant="subtitle1" sx={{ mb: 1 }}>
							{t('nfc:batchProducts')}
						</Typography>
						<Typography variant="body2" sx={{ mb: 2 }}>
							{t('nfc:batchProductsHelp')}
						</Typography>

						<NfcCardCartPicker cart={cart} disabled={locked} optional />

						<Divider sx={{ my: 3 }} />

						<Typography variant="h6" sx={{ mb: 2 }}>
							{t('nfc:batchCards')}
						</Typography>

						{scanning ? (
							<Button
								variant="contained"
								color="secondary"
								size="large"
								startIcon={<Pause />}
								onClick={stop}
								sx={{ minHeight: 48 }}
							>
								{t('nfc:batchPause')}
							</Button>
						) : (
							<Button
								variant="contained"
								color="primary"
								size="large"
								startIcon={<Nfc />}
								onClick={start}
								disabled={locked}
								sx={{ minHeight: 48 }}
							>
								{uids.length > 0 ? t('nfc:batchResume') : t('nfc:batchStart')}
							</Button>
						)}

						{scanning && (
							<Alert severity="info" sx={{ mt: 2 }}>
								{t('nfc:batchScanning')}
							</Alert>
						)}

						{!!readError && (
							<Alert severity="error" sx={{ mt: 2 }}>
								{readError}
							</Alert>
						)}

						{tooManyCards && (
							<Alert severity="warning" sx={{ mt: 2 }}>
								{t('nfc:batchTooMany', { max: NFC_BATCH_MAX_CARDS })}
							</Alert>
						)}

						<Typography variant="subtitle1" sx={{ mt: 2 }}>
							{t('nfc:batchCount', { count: uids.length })}
						</Typography>

						{uids.length === 0 && (
							<Typography variant="body2" sx={{ mt: 1 }}>
								{t('nfc:batchEmpty')}
							</Typography>
						)}

						<List dense>
							{uids.map((uid, index) => {
								const failure = failureOf(uid);

								return (
									<ListItem
										key={uid}
										divider
										disableGutters
										secondaryAction={
											<IconButton
												edge="end"
												aria-label={t('nfc:batchRemove', { number: uids.length - index })}
												onClick={() => remove(uid)}
												disabled={locked}
												color={failure ? 'error' : 'default'}
											>
												<DeleteOutline />
											</IconButton>
										}
									>
										<ListItemText
											primary={<Typography>{cardName(index)}</Typography>}
											secondary={failure}
											secondaryTypographyProps={{ color: 'error' }}
										/>
									</ListItem>
								);
							})}
						</List>
					</FormGroup>
				)}
			</DialogContent>

			{/* Out of the scrolling content, so on a phone they stay at the bottom, where the thumb reaches them */}
			<DialogActions
				sx={{
					flexDirection: isMobile ? 'column' : 'row',
					alignItems: 'stretch',
					gap: 1,
					padding: 2,
					'& > :not(:first-of-type)': { marginLeft: 0 },
				}}
			>
				{results ? (
					<Button
						variant="contained"
						color="secondary"
						size="large"
						fullWidth={isMobile}
						startIcon={<Close />}
						onClick={close}
					>
						{t('common:close')}
					</Button>
				) : (
					<>
						<Button
							variant="contained"
							color="secondary"
							size="large"
							fullWidth={isMobile}
							startIcon={<Close />}
							onClick={close}
							disabled={saving}
						>
							{t('common:cancel')}
						</Button>

						<Button
							variant="contained"
							color="primary"
							size="large"
							fullWidth={isMobile}
							startIcon={<Save />}
							onClick={create}
							disabled={!canCreate}
						>
							{uncertain
								? t('nfc:batchRetry', { count: uids.length })
								: t('nfc:batchCreate', { count: uids.length })}
						</Button>
					</>
				)}
			</DialogActions>
		</Dialog>
	);
}

/**
 * An amount typed in pesos, with a comma or a point for the cents, or null when it is not a
 * positive one.
 *
 * @param text What was typed
 */
function parseAmount(text: string): number | null {
	const trimmed = text.trim().replace(',', '.');
	if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null;

	const amount = Number(trimmed);
	return amount > 0 ? amount : null;
}
